import { observable, action } from 'mobx';

import messagingService from 'services/MessagingService/MessagingService';
import {
  AUTOBILLING_OPTIN,
  AUTOBILLING_OPTIN_ERROR,
  AUTOBILLING_OPTIN_RESPONSE,
  GENERIC_ERROR,
  GET_AUTOBILLING_STATUS_ERROR,
  GET_AUTOBILLING_STATUS_RESPONSE,
  TRIGGER_AUTOBILLING,
  TRIGGER_AUTOBILLING_RESPONSE,
  UPDATE_BALANCE,
  USER_LOGGED_OUT,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import PaymentService from 'services/PaymentService';
import { MINUTE_AS_MILLISECONDS } from 'enum/timeFormat';


const AUTO_TRIGGER_DELAY = 5 * MINUTE_AS_MILLISECONDS;

class PaymentStore {
  constructor(messagingService) {
    this.messagingService = messagingService();
    this.messagingService.on(GET_AUTOBILLING_STATUS_RESPONSE, this.handleAutoBillingStatusResponse);
    this.messagingService.on(GET_AUTOBILLING_STATUS_ERROR, this.handleAutoBillingStatusError);
    this.messagingService.on(AUTOBILLING_OPTIN_ERROR, this.handleAutoBillingOptInError);
    this.messagingService.on(AUTOBILLING_OPTIN_RESPONSE, this.handleAutoBillingOptInResponse);
    this.messagingService.on(UPDATE_BALANCE, this.handleUpdateBalance);
    this.messagingService.on(TRIGGER_AUTOBILLING_RESPONSE, this.handleTriggerAutoBilling);
    this.messagingService.on(USER_LOGGED_OUT, this.resetPaymentStore);
  }

  autoBillingStatuses = {
    DISABLED: 'DISABLED',
    ENABLED: 'ENABLED',
    PENDING: 'PENDING',
  };

  destructor() {
    clearTimeout(this.autoBillingPending);
    this.messagingService.off(TRIGGER_AUTOBILLING_RESPONSE, this.handleTriggerAutoBilling);
    this.messagingService.off(GET_AUTOBILLING_STATUS_RESPONSE, this.handleAutoBillingStatusResponse);
    this.messagingService.off(GET_AUTOBILLING_STATUS_ERROR, this.handleAutoBillingStatusError);
    this.messagingService.off(AUTOBILLING_OPTIN_ERROR, this.handleAutoBillingOptInError);
    this.messagingService.off(AUTOBILLING_OPTIN_RESPONSE, this.handleAutoBillingOptInResponse);
    this.messagingService.off(UPDATE_BALANCE, this.handleUpdateBalance);
    this.messagingService.off(USER_LOGGED_OUT, this.resetPaymentStore);
  }

  @observable autoBillingStatus = this.autoBillingStatuses.DISABLED;
  @observable threshold = 0;
  @observable autoBillingProduct = null;
  @observable balance = 0;
  @observable username = null;
  @observable balanceFetched = false;

  @action resetPaymentStore = () => {
    this.autoBillingStatus = this.autoBillingStatuses.DISABLED;
    this.threshold = 0;
    this.autoBillingProduct = null;
    this.balance = 0;
    this.username = null;
    this.balanceFetched = false;
  };

  @action setBalanceFetched = (flag) => {
    this.balanceFetched = flag;
  };

  handleUpdateBalance = (balance) => {
    const balanceChanged = this.balance !== parseInt(balance, 10) && this.balanceFetched;
    this.setBalanceFetched(true);
    if (!isNaN(balance)) {
      this.balance = balance;
    }
    if (this.threshold * 1 >= this.balance * 1 && balanceChanged) {
      if (this.autoBillingStatus === this.autoBillingStatuses.PENDING) {
        this.showAutoBillingModal();
      }
      if (this.autoBillingStatus === this.autoBillingStatuses.ENABLED && balanceChanged) {
        if (!this.autoBillingPending) {
          this.messagingService.emit(TRIGGER_AUTOBILLING, { username: this.username });
        }
      }
    }
  };

  showAutoBillingModal = () => {
    ModalService.showModal(ModalService.MODAL_KEYS.AUTO_RELOAD_MODAL, {
      showClose: false,
      quantity: this.autoBillingProduct.quantity,
    });
  };

  handleTriggerAutoBilling = ({ status }) => {
    if (status === this.autoBillingStatuses.PENDING) {
      this.autoBillingPending = setTimeout(() => {
        this.autoBillingPending = null;
        this.messagingService.emit(TRIGGER_AUTOBILLING, { username: this.username });
      }, AUTO_TRIGGER_DELAY);
    }
  };

  handleAutoBillingOptInResponse = (data) => {
    this.autoBillingStatus = data.status;
    this.hideModal();
  };

  @action optInForAutoBilling = (status) => {
    this.messagingService.emit(AUTOBILLING_OPTIN, { status, username: this.username });
  };

  @action getAutoBillingStatus = (username) => {
    this.username = username;
    PaymentService.getAutoBillingStatus(username);
  };

  @action resetAutoBillingData = () => {
    this.autoBillingProduct = null;
    this.threshold = 0;
    this.autoBillingStatus = this.autoBillingStatuses.DISABLED;
  };

  handleAutoBillingStatusResponse = (data) => {
    const { status, threshold, product } = data;
    if (status) {
      this.autoBillingStatus = status;
    }
    if (threshold) {
      this.threshold = threshold;
    }
    if (product) {
      this.autoBillingProduct = product;
    }
  };

  hideModal = () => {
    ModalService.hideModal();
  };

  handleAutoBillingStatusError = () => {
    this.resetAutoBillingData();
  };

  handleAutoBillingOptInError = (e) => {
    this.messagingService.emit(GENERIC_ERROR, e);
    this.hideModal();
  };
}

export const PaymentStoreClass = PaymentStore;

export default new PaymentStore(messagingService);
